import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaDownload, FaEye, FaTrash, FaPlay } from "react-icons/fa";
import { FaImage } from "react-icons/fa6";
import "./EditingViewGallery.css";
import { 
  getAllGalleryImages, 
  deleteSingleImage,
  deleteSelectedImages,
  getGalleryZipLink,
  downloadGalleryZipFromCdn,
} from "../../../../utils/APIs/UploadGalleryApis";
import { getAccessToken } from "../../../../utils/APIs/commonHeadApiLogic";
import { toast } from "react-toastify";
import {
  appendLightcasePoolLinks,
  bindLightcaseLinks,
  clearLightcasePool,
  destroyLightcaseBindings,
  isVideoMediaKey,
  openLightcaseItem,
  resolveGalleryMediaUrl,
} from "../../UploadGallery/ViewGalleryPhoto/lightcaseGallery";

const loadedImagesCache = new Set();

class MediaLoadQueue {
  constructor(concurrency = 4) {
    this.concurrency = concurrency;
    this.running = 0;
    this.queue = [];
  }

  register(onStart) {
    const item = { onStart };
    this.queue.push(item);
    this.next();
    return () => {
      this.queue = this.queue.filter((q) => q !== item);
    };
  }

  next() {
    if (this.running >= this.concurrency || this.queue.length === 0) return;

    this.running++;
    const item = this.queue.shift();

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      this.running--;
      this.next();
    };

    item.onStart(finish);
    setTimeout(finish, 2500); // 2.5s safety timeout
  }
}

const mediaLoadQueue = new MediaLoadQueue(4);

const VirtualGalleryItem = ({ children, isVideo, id, src }) => {
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (hasBeenVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenVisible(true);
        }
      },
      {
        rootMargin: "1200px 0px 1200px 0px",
        threshold: 0.01,
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasBeenVisible]);

  const placeholderStyle = { 
    aspectRatio: "16/10", 
    width: "100%", 
    background: "#eee", 
    borderRadius: "4px" 
  };

  const isAlreadyCached = src && loadedImagesCache.has(src);
  const placeholderClass = `gallery-placeholder-box ${isAlreadyCached ? "" : "loading-skeleton"}`;

  return (
    <div ref={containerRef} className="virtual-gallery-item-wrapper">
      {hasBeenVisible ? (
        children
      ) : (
        <div style={placeholderStyle} className={placeholderClass} />
      )}
    </div>
  );
};


const GalleryItem = React.memo(({
  src,
  isVideo,
  fileKey,
  id,
  isSelected,
  onOpenPreview,
  onDownload,
  onDelete,
  onToggleSelect
}) => {
  const [shouldLoad, setShouldLoad] = useState(() => loadedImagesCache.has(src));
  const [isLoaded, setIsLoaded] = useState(() => loadedImagesCache.has(src));
  const mediaRef = useRef(null);
  const finishLoadRef = useRef(null);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    loadedImagesCache.add(src);
    if (finishLoadRef.current) {
      finishLoadRef.current();
      finishLoadRef.current = null;
    }
  }, [src]);

  const handleError = useCallback(() => {
    if (finishLoadRef.current) {
      finishLoadRef.current();
      finishLoadRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (shouldLoad) return;

    const unregister = mediaLoadQueue.register((finish) => {
      finishLoadRef.current = finish;
      setShouldLoad(true);
    });

    return () => {
      unregister();
      if (finishLoadRef.current) {
        finishLoadRef.current();
        finishLoadRef.current = null;
      }
    };
  }, [src, shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) return;
    const el = mediaRef.current;
    if (!el) return;

    if (isVideo) {
      if (el.readyState >= 1) {
        handleLoad();
      }
    } else {
      if (el.complete) {
        handleLoad();
      }
    }
  }, [isVideo, handleLoad, shouldLoad]);

  return (
    <div className={`gallery-box video-box ${isSelected ? 'selected' : ''} ${!isLoaded ? 'loading-skeleton' : ''}`}>
      {shouldLoad ? (
        isVideo ? (
          <video
            ref={mediaRef}
            src={src}
            className="gallery-video"
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={handleLoad}
            onError={handleError}
            style={{ visibility: isLoaded ? "visible" : "hidden" }}
            onClick={() => onOpenPreview(id)}
          />
        ) : (
          <img
            ref={mediaRef}
            src={src}
            alt="gallery"
            decoding="async"
            onLoad={handleLoad}
            onError={handleError}
            style={{ visibility: isLoaded ? "visible" : "hidden" }}
            onClick={() => onOpenPreview(id)}
          />
        )
      ) : (
        <div style={{ width: "100%", height: "100%" }} onClick={() => onOpenPreview(id)} />
      )}
      
      {isVideo && shouldLoad && isLoaded && (
        <div className="play-overlay" onClick={() => onOpenPreview(id)}>
          <div className="play-icon">
            <FaPlay className="play-triangle" />
          </div>
        </div>
      )}

      <div
        className="gallery-hover"
        onClick={(e) => {
          if (!e.target.closest("button") && !e.target.closest("input")) {
            onOpenPreview(id);
          }
        }}
      >
        <div className="gallery-actions-hover">
          <button
            className="gallery-hover-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(fileKey);
            }}
          >
            <FaDownload />
          </button>
          <button
            className="gallery-hover-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(id, fileKey);
            }}
          >
            <FaTrash />
          </button>
          <button
            className="gallery-hover-btn"
            onClick={(e) => {
              e.stopPropagation();
              onOpenPreview(id);
            }}
          >
            <FaEye />
          </button>
        </div>
        <div className="gallery-checkbox">
          <input 
            type="checkbox" 
            checked={isSelected}
            onChange={() => onToggleSelect(src)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
    </div>
  );
});

const EditingViewGallery = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  
  const [activeSource, setActiveSource] = useState("Photographer Media");
  const [activePlan, setActivePlan] = useState("standard");
  const [selectedItems, setSelectedItems] = useState([]);
  const [galleryData, setGalleryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const stored = sessionStorage.getItem("isSidebarOpen");
    return stored !== null ? JSON.parse(stored) : true;
  });

  const LIMIT = 33;
  const isFetchingRef = useRef(false);
  const loaderRef = useRef(null);
  const lightcasePoolRef = useRef(null);

  const availableSources = ["User Media", "Photographer Media"];
  const availablePlans = ["standard", "premium",];
  const lightcaseGroup = `editing-gallery-${bookingId}-${activeSource}-${activePlan}`;

  useEffect(() => {
    const interval = setInterval(() => {
      const stored = sessionStorage.getItem("isSidebarOpen");
      const parsed = stored !== null ? JSON.parse(stored) : true;

      if (parsed !== isSidebarOpen) {
        setIsSidebarOpen(parsed);
      }
    }, 10);

    return () => clearInterval(interval);
  }, [isSidebarOpen]);

  const fetchGallery = useCallback(async (currentPage) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      setLoading(true);
      const sourceParam = activeSource === "Photographer Media" ? "photographer" : "user";
      let categoryParam = activePlan;
      if (activeSource === "User Media" && (categoryParam === "custom" || categoryParam === "editing")) {
        categoryParam = "standard";
      }
      const response = await getAllGalleryImages(currentPage, LIMIT, bookingId, categoryParam, sourceParam);

      if (response?.data?.success) {
        const files = response?.data?.data || [];
        const images = files.map((item, index) => ({
          id: item._id || `${currentPage}-${index}`,
          key: item.key,
          url: item.imageUrl,
          imageUrl: item.imageUrl,
          clientId: item.clientId,
          photographerId: item.photographerId,
          category: item.category
        }));

        setGalleryData((prev) =>
          currentPage === 1 ? images : [...prev, ...images]
        );
        setHasMore(response?.data?.pagination?.hasMore);
      }
    } catch (error) {
      console.error("Gallery API Error:", error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [bookingId, activeSource, activePlan]);

  // Reset on Category Change
  useEffect(() => {
    setLoading(true);
    setGalleryData([]);
    setPage(1);
    setHasMore(true);
    setSelectedItems([]); 
    isFetchingRef.current = false;
  }, [activeSource, activePlan]);

  useEffect(() => {
    fetchGallery(1);
  }, [bookingId, fetchGallery]);

  useEffect(() => {
    const poolNode = lightcasePoolRef.current;
    return () => {
      destroyLightcaseBindings();
      clearLightcasePool(poolNode);
    };
  }, []);

  useEffect(() => {
    const container = lightcasePoolRef.current;
    if (!container) return;

    clearLightcasePool(container);

    if (galleryData.length === 0) return;

    let cancelled = false;
    appendLightcasePoolLinks(container, galleryData, lightcaseGroup, 0, bookingId).then(() => {
      if (!cancelled) {
        bindLightcaseLinks(container);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [galleryData, lightcaseGroup, bookingId]);

  useEffect(() => {
    if (page > 1) {
      fetchGallery(page);
    }
  }, [page, fetchGallery]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !isFetchingRef.current) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  const handleBack = () => navigate(-1);

  const toggleSelect = useCallback((url) => {
    setSelectedItems((prev) =>
      prev.includes(url)
        ? prev.filter((item) => item !== url)
        : [...prev, url]
    );
  }, []);

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    if (checked) {
      setSelectedItems(galleryData.map((item) => item.imageUrl));
    } else {
      setSelectedItems([]);
    }
  };

  const openPreview = useCallback((id) => {
    setGalleryData((prev) => {
      const item = prev.find((entry) => entry.id === id);
      if (item) {
        if (!openLightcaseItem(id, undefined, bookingId, item) && item.imageUrl) {
          window.open(item.imageUrl, "_blank");
        }
      }
      return prev;
    });
  }, [bookingId]);

  const deleteFile = useCallback(async (id, key) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;
    
    try {
      setLoading(true);
      await deleteSingleImage({ bookingid: bookingId, key });
      setGalleryData(prev => prev.filter(f => f.key !== key));
      setSelectedItems(prev => prev.filter(item => item !== key));
    } catch (error) {
      console.error("Delete Error:", error);
      toast.error("Failed to delete file");
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  const buildZipFilterParams = () => {
    let categoryParam = activePlan;
    if (activeSource === "User Media" && (categoryParam === "custom" || categoryParam === "editing")) {
      categoryParam = "standard";
    }

    return {
      bookingId,
      category: categoryParam || "",
      source: activeSource === "Photographer Media" ? "photographer" : "user",
    };
  };


  const handleDownloadZip = async () => {
    try {
      const zipFilters = buildZipFilterParams();
      await downloadGalleryZipFromCdn(zipFilters, {
        onWaiting: () => toast.info("Preparing download from cloud storage..."),
      });
    } catch (error) {
      console.error("Download Zip Error:", error);
      toast.error(error.message || "ZIP download failed");
    }
  };

  useEffect(() => {
    if (!bookingId || galleryData.length === 0) return;
    const zipFilters = buildZipFilterParams();
    getGalleryZipLink(zipFilters).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, activePlan, activeSource, galleryData.length]);

  const deleteSelected = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedItems.length} files?`)) return;

    try {
      setLoading(true);
      const selectedKeys = galleryData
        .filter((item) => selectedItems.includes(item.imageUrl))
        .map((item) => item.key);

      await deleteSelectedImages({ bookingId: bookingId, key: selectedKeys });

      setGalleryData((prev) =>
        prev.filter((item) => !selectedKeys.includes(item.key))
      );
      setSelectedItems([]);
      toast.success("Selected images deleted successfully");
    } catch (error) {
      console.error("Delete selected failed", error);
      toast.error("Delete selected failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadSelected = async () => {
    try {
      const selectedKeys = galleryData
        .filter((item) => selectedItems.includes(item.imageUrl))
        .map((item) => item.key);

      if (selectedKeys.length === 0) return;

      const zipFilters = buildZipFilterParams();
      await downloadGalleryZipFromCdn(
        {
          ...zipFilters,
          keys: selectedKeys,
        },
        {
          onWaiting: () => toast.info("Preparing selected files from cloud storage..."),
        }
      );
    } catch (error) {
      console.error("Download Selected Error:", error);
      toast.error(error.message || "Download failed");
    }
  };

  const downloadFile = useCallback(async (key) => {
    try {
      const token = getAccessToken();
      const baseUrl = (process.env.REACT_APP_PHOTOGRAPHY_ADMIN_BASE_API_URL || "").trim();

      const params = new URLSearchParams({
        key: key,
        bookingId: bookingId,
        token: token
      });

      window.location.href = `${baseUrl}/api/admins/downloadSingleFile?${params.toString()}`;
    } catch (error) {
      console.error("Single Download Error:", error);
    }
  }, [bookingId]);

  const isVideoKey = isVideoMediaKey;

  const buildRows = () => {
    if (loading && galleryData.length === 0) {
      return (
        <div className="gallery-grid-layout">
          {Array.from({ length: 8 }).map((_, idx) => (
            <div className="gallery-box loading-skeleton" key={`initial-skeleton-${idx}`} />
          ))}
        </div>
      );
    }

    if (galleryData.length === 0) return null;

    return (
      <div className="gallery-grid-layout">
        {galleryData.map((item) => (
          <VirtualGalleryItem key={item.key} isVideo={isVideoKey(item.key)} id={item.id} src={resolveGalleryMediaUrl(bookingId, item)}>
            <GalleryItem
              src={resolveGalleryMediaUrl(bookingId, item)}
              isVideo={isVideoKey(item.key)}
              fileKey={item.key}
              id={item.id}
              isSelected={selectedItems.includes(item.imageUrl)}
              onOpenPreview={openPreview}
              onDownload={downloadFile}
              onDelete={deleteFile}
              onToggleSelect={toggleSelect}
            />
          </VirtualGalleryItem>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`content-container ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
      style={{ marginTop: "100px" }}
    >
      <div className="page-inner-wrapper">
        <h1 className="mb-4">View Gallery</h1>
        <div className="container gallery-container">
          <div
            ref={lightcasePoolRef}
            className="lightcase-gallery-pool"
            aria-hidden="true"
          />
          
          <div className="mb-4">
            <button onClick={handleBack} className="admin-back-btn">
              ← Back
            </button>
          </div>

          {/* TABS SECTION */}
          <div className="gallery-tabs-wrapper">
            {/* PLAN TABS */}
            <div className="gallery-tabs-row">
              {availablePlans.map(plan => (
                <button
                  key={plan}
                  className={`plan-tab ${activePlan === plan ? "active" : ""}`}
                  onClick={() => setActivePlan(plan)}
                >
                  {plan}
                </button>
              ))}
            </div>

            {/* SOURCE TABS */}
            <div className="gallery-tabs-row">
              {availableSources.map(src => (
                <button
                  key={src}
                  className={`category-tab ${activeSource === src ? "active" : ""}`}
                  onClick={() => setActiveSource(src)}
                >
                  {src}
                </button>
              ))}
            </div>
          </div>

          <div className="gallery-header">
            <div className="gallery-left">
              <div className="select-all-wrapper" onClick={() => document.getElementById("selectAll").click()}>
                <input 
                  type="checkbox" 
                  id="selectAll"
                  checked={galleryData.length > 0 && selectedItems.length === galleryData.length}
                  onChange={handleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                />
                <label htmlFor="selectAll">
                  Select All
                </label>
              </div>

              {selectedItems.length > 0 && (
                <>
                  <button className="download-selected-btn" onClick={downloadSelected}>
                    Download Selected ({selectedItems.length})
                  </button>
                  <button className="delete-selected-btn" onClick={deleteSelected}>
                    Delete Selected ({selectedItems.length})
                  </button>
                </>
              )}
            </div>

            <div className="gallery-actions">
              {galleryData.length > 0 && (
                <button className="gallery-btn" onClick={handleDownloadZip}>Download Zip</button>
              )}
            </div>
          </div>

          {buildRows()}



          {hasMore && (
            <div ref={loaderRef} style={{ textAlign: "center", padding: "20px" }}>
              {loading && (
                <div className="spinner-border" role="status" style={{ width: "2.5rem", height: "2.5rem", color: "var(--primary-color)" }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
              )}
            </div>
          )}

          {galleryData.length === 0 && !loading && (
             <div className="gallery-no-images">
                <FaImage className="gallery-no-images-icon" />
                <h3>Deliverables coming soon</h3>
                <p>This section is currently empty.</p>
             </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default EditingViewGallery;
